// ───────────────────────────────────────────────────────────
// DEBUG UTIL: log key values before/after forcing full height
// ───────────────────────────────────────────────────────────
export function debugHeights(label) {
  console.log(`${label} — window.innerHeight:`, window.innerHeight)
  console.log(
    `${label} — window.visualViewport.height:`,
    window.visualViewport?.height,
  )
  console.log(`${label} — window.screen.height:`, window.screen.height)
  console.log(
    `${label} — html.clientHeight:`,
    document.documentElement.clientHeight,
  )
  console.log(`${label} — body.clientHeight:`, document.body.clientHeight)
  const rootEl = document.getElementById('root')
  console.log(
    `${label} — #root.clientHeight:`,
    rootEl ? rootEl.clientHeight : 'n/a',
  )
}

/**
 * For PWA, save current viewport height (in pixels) to localStorage
 */
export function saveViewportHeight() {
  // We still capture innerHeight at login time in case of SSO,
  // but our restoration always uses screen.height
  const heightPx = window.innerHeight
  localStorage.setItem('savedVh', `${heightPx}px`)
}

/**
 * For PWA, force full-height on html, body, #root using screen.height
 */
export function restoreFullHeight(isDeviceMobile = true) {
  if (!isDeviceMobile) {
    return
  }
  debugHeights('Before restore')

  // Detect landscape mode on mobile PWA
  const isLandscape =
    window.matchMedia && window.matchMedia('(orientation: landscape)').matches
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer?.startsWith('android-app://')

  // Temporarily “opt out” of visual‐viewport clamping
  document.documentElement.setAttribute('data-ignore-visual-clamp', '')

  let fullHeightPx
  if (isLandscape && isStandalone) {
    // In landscape PWA on mobile, use the actual viewport height
    const vh = window.visualViewport?.height || window.innerHeight
    fullHeightPx = `${vh}px`
    document.documentElement.style.setProperty(
      '--vh',
      `${vh / 100}px`,
      'important',
    )
  } else {
    // Portrait or non-PWA: use screen.height (original logic)
    fullHeightPx = `${window.screen.height}px`
    document.documentElement.style.setProperty(
      '--vh',
      `${window.screen.height / 100}px`,
      'important',
    )
  }

  document.documentElement.style.setProperty(
    'height',
    fullHeightPx,
    'important',
  )
  document.documentElement.style.setProperty(
    'min-height',
    fullHeightPx,
    'important',
  )
  document.body.style.setProperty('height', fullHeightPx, 'important')
  document.body.style.setProperty('min-height', fullHeightPx, 'important')
  const rootEl = document.getElementById('root')
  if (rootEl) {
    rootEl.style.setProperty('height', fullHeightPx, 'important')
    rootEl.style.setProperty('min-height', fullHeightPx, 'important')
  }

  // “Poke” Safari into repaint
  document.documentElement.style.transform = 'translateZ(0)'
  document.body.style.transform = 'translateZ(0)'
  if (rootEl) rootEl.style.transform = 'translateZ(0)'

  setTimeout(() => {
    // Re‐apply the same forcing to ensure no re‐clamp
    document.documentElement.setAttribute('data-ignore-visual-clamp', '')
    document.documentElement.style.transform = 'translateZ(0)'
    document.body.style.transform = 'translateZ(0)'
    if (rootEl) rootEl.style.transform = 'translateZ(0)'
    // Debug
    debugHeights('After restore')
  }, 100)
}

/**
 * For PWA, Register event listeners to run restoreFullHeight
 * on load, focus, pageshow, orientationchange
 */
export function registerViewportRestoreListeners(isDeviceMobile = true) {
  if (!isDeviceMobile) {
    return
  }
  // On initial page load (before React renders)
  window.addEventListener('DOMContentLoaded', restoreFullHeight)

  // When coming back from background or from SSO redirect
  window.addEventListener('focus', restoreFullHeight)
  window.addEventListener('pageshow', () => restoreFullHeight())

  // On orientation change, wait a moment so screen.height is updated
  window.addEventListener('orientationchange', () => {
    setTimeout(restoreFullHeight, 50)
  })
}

/**
 * For PWA, adjust the chat input position when the keyboard is open
 */
export function bindKeyboardAdjustments(isDeviceMobile = true) {
  if (!isDeviceMobile) {
    return
  }
  const inputWrapper = document.getElementById('chat-input-wrapper')
  if (!inputWrapper || !window.visualViewport) return

  // 1) Keep CSS var --input-height updated
  function updateInputHeight() {
    const h = inputWrapper.offsetHeight
    document.documentElement.style.setProperty('--input-height', `${h}px`)
  }
  updateInputHeight()
  new ResizeObserver(updateInputHeight).observe(inputWrapper)

  // 2) Shift input above keyboard on visualViewport resize
  function adjustPosition() {
    const bottomInset = window.innerHeight - window.visualViewport.height
    inputWrapper.style.transform = `translateY(-${bottomInset}px)`
  }

  window.visualViewport.addEventListener('resize', () => {
    updateInputHeight()
    adjustPosition()
  })

  // initial adjustment
  adjustPosition()
}
